import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { NzMessageService } from 'ng-zorro-antd/message';
import { BehaviorSubject, Subscription } from 'rxjs';

import { ActionService, cleanObject, CustomFormatter, ISitemap, ISitemapCommand, ISitemapModel, SITEMAP } from 'pbc.angular';

import { IPostZeitUnterartResponse, IZeitUnterart } from 'fa-kt.types';
import { ZeitUnterartsService } from '../../../query';
import { PostZeitUnterartCommandService } from '../service';

interface IFormValue {
  zeitUnterart?: IZeitUnterart;
}

@Component({
  selector: 'fa-kt-post-zeit-unterart',
  templateUrl: './post-zeit-unterart.component.html',
  styleUrls: ['./post-zeit-unterart.component.css'],
})
export class PostZeitUnterartCommandComponent implements OnInit, OnDestroy {
  command!: ISitemapCommand;
  model!: ISitemapModel;

  subscriptions: Subscription[] = [];
  form!: FormGroup;
  $loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  $valid: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  CustomFormatter = CustomFormatter;

  @Input() set loading(loading: boolean) {
    this.$loading.next(this.$loading.getValue() || loading);
  }
  @Input() set value(value: IFormValue) {
    this.patch(value);
  }
  @Output() valid: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() finished: EventEmitter<IPostZeitUnterartResponse> = new EventEmitter<IPostZeitUnterartResponse>();

  constructor(
    @Inject(SITEMAP) private sitemap: ISitemap,
    private fb: FormBuilder,
    private actions: ActionService,
    private message: NzMessageService,
    public zeitUnterarts: ZeitUnterartsService,
    public postZeitUnterart: PostZeitUnterartCommandService /* << dependencies    */ /*    dependencies >> */,
  ) {
    this.command = sitemap.ZEITEN.Commands.POST_ZEIT_UNTERART;
    this.model = sitemap.ZEITEN.Models.ZEIT_UNTERART;
    /* << constructor    */
    /*    constructor >> */
    this.form = this.fb.group({
      zeitUnterart: this.fb.group({
        id: [null, []],
        zeitArt: [null, [Validators.required]],
        name: [null, [Validators.required]],
        beschreibung: [null, []],
      }),
    });
    this.patch();
  }

  ngOnInit() {
    this.subscriptions.push(
      this.form.valueChanges.subscribe((value: any) => {
        this.valid.emit(this.form.valid);
        if (this.form.valid) {
          this.actions.subscribe({ key: this.command.translation, action: async () => await this.submit() });
        } else {
          this.actions.unsubscribe(this.command.translation);
        }
      }),
      this.id?.valueChanges.subscribe(async (id) => {
        /* << on-id-change    */
        /*    on-id-change >> */
      }) as Subscription,
      this.zeitArt?.valueChanges.subscribe(async (zeitArt) => {
        /* << on-zeit-art-change    */
        /*    on-zeit-art-change >> */
      }) as Subscription,
      this.name?.valueChanges.subscribe(async (name) => {
        /* << on-name-change    */
        /*    on-name-change >> */
      }) as Subscription,
      this.beschreibung?.valueChanges.subscribe(async (beschreibung) => {
        /* << on-beschreibung-change    */
        /*    on-beschreibung-change >> */
      }) as Subscription,
      /* << subscibe    */
      /*    subscibe >> */
    );
    /* << init    */
    /*    init >> */
  }

  ngOnDestroy() {
    this.actions.unsubscribe(this.command.translation);
    this.subscriptions.filter(($: Subscription) => $ && !$.closed).forEach(($: Subscription) => $.unsubscribe());
  }

  private patch(value?: IFormValue) {
    let zeitUnterart = {
      id: null,
      zeitArt: null,
      name: null,
      beschreibung: null,
    };
    if (value && value.zeitUnterart) {
      zeitUnterart = {
        ...zeitUnterart,
        ...cleanObject(value.zeitUnterart),
      };
    }
    this.form.patchValue({ zeitUnterart });
    /* << custom-stuff    */
    /*    custom-stuff >> */
    this.update();
  }

  update() {
    this.$loading.next(true);
    this.form.markAsDirty();
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
    this.$valid.next(this.form.valid);
    this.$loading.next(false);
  }

  async submit() {
    this.update();
    if (!this.form.valid) {
      return;
    }
    this.$loading.next(true);
    try {
      const payload = cleanObject(this.form.getRawValue());
      if (payload && payload.zeitUnterart) {
        payload.zeitUnterart = cleanObject(payload.zeitUnterart);
      }
      const response: IPostZeitUnterartResponse | undefined = await this.postZeitUnterart.request(payload);
      this.finished.next(response);
    } catch (error: any) {
      this.message.error(error.message);
      for (const validation of error.errors ? error.errors : []) {
        for (const children of validation.children ? validation.children : []) {
          const message = Object.values(children.constraints).reduce((acc, val) => acc + ' ' + val, '');
          this.form.controls.objektArt.get(children.property)?.setErrors({ server: message });
        }
      }
    }
    this.$loading.next(false);
  }

  get id() {
    return this.form.controls.zeitUnterart.get('id');
  }

  get zeitArt() {
    return this.form.controls.zeitUnterart.get('zeitArt');
  }

  get name() {
    return this.form.controls.zeitUnterart.get('name');
  }

  get beschreibung() {
    return this.form.controls.zeitUnterart.get('beschreibung');
  }
}
